import Button from "@/components/bricks/button/Button";
import CloseIcon from "../../../../../icons/CloseIcon";
import EditIcon from "../../../../../icons/EditIcon";
import StamopIcon from "../../../../../icons/StamopIcon";
import FirmaStamKontakt from "./FirmaStamKontakt";
import FirmaStamFakturering from "./FirmaStamFakturering";
import FirmaStamBemaerkninger from "./FirmaStamBemaerkninger";
import FirmaStamMeta2 from "./FirmaStamMeta2";
import FirmaStamMeta1 from "./FirmaStamMeta1";
import { FirmaDto } from "../../../../../../interfaces/schema";
import { useFirmaTopFormik } from "./useFirmaTopFormik";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../../context/UserContext";
import Spinner from "../../../../../spinner/Spinner";

const StamoplysningerTop = ({
  firma,
  editMode,
  setEditMode,
  invalidateData,
}: {
  firma: FirmaDto;
  editMode: boolean;
  setEditMode: (editmode: boolean) => void;
  invalidateData: InvalidateDataFunction<any>;
}) => {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "BasisWrite");
  const { formik, isLoading, error, isError, resetMutation } =
    useFirmaTopFormik(firma, {
      setEditMode,
      invalidateData,
    });
  const componentProps = { firma, editMode, formik };
  function toggleEditMode() {
    if (editMode) {
      resetMutation();
      formik.resetForm();
    }
    setEditMode(!editMode);
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <div
        className={`c-card c-card--editable${editMode ? " is-editable" : ""}`}
      >
        <div className="d-flex justify-content-between align-items-center mb-3 mt-1">
          <h3 className="d-flex align-items-center gap-2">
            <span className="c-theme-icon bg-primary-light text-primary">
              <StamopIcon />
            </span>{" "}
            Stamoplysninger
          </h3>
          {canWrite && (
            <Button
              type="button"
              transparent
              color="primary"
              onClick={toggleEditMode}
              className="edit-trigger"
            >
              {editMode && (
                <>
                  Luk <CloseIcon />
                </>
              )}
              {!editMode && (
                <>
                  Redigér
                  <EditIcon />
                </>
              )}
            </Button>
          )}
        </div>

        <div className="row g-4">
          <div className="col-12 col-xl-6 d-flex flex-column gap-4">
            <FirmaStamMeta1 {...componentProps} />
            <FirmaStamMeta2 firma={firma} />
          </div>
          <div className="col-12 col-xl-6 d-flex flex-column gap-4">
            <FirmaStamKontakt {...componentProps} />
            <FirmaStamFakturering {...componentProps} />
            <FirmaStamBemaerkninger {...componentProps} />
          </div>

          {editMode && (
            <div className="d-grid gap-2 d-md-flex justify-content-md-end align-items-center">
              {isError && <ErrorMessage error={error} />}
              <Button
                color="secondary"
                type="button"
                className="me-md-2"
                onClick={toggleEditMode}
              >
                Annuller
              </Button>
              <Button
                color="primary"
                type="submit"
                onClick={() => {}}
                disabled={!formik.dirty}
              >
                {isLoading && <Spinner className="text-light" size={16} />} Gem
              </Button>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

export type FirmaFields = Omit<FirmaDto, "selskabstype"> & {
  selskabstype: string;
};

export type FirmaPatchDTO = Omit<FirmaFields, "id">;

export default StamoplysningerTop;

function ErrorMessage(props: { error: any }) {
  const message =
    props.error?.message ?? props.error?.data?.Message ?? "Der skete en fejl";
  return <small className={`text-danger mt-1 fw-semibold`}>{message}</small>;
}
